import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FiRefreshCw, FiAlertCircle, FiCheck, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { createRun } from "../../services/runService";
import { fetchInceptBenchVersions } from "../../services/configService";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../services/supabase";

/**
 * Re-Evaluate Button Component
 * Allows creating a new run that reuses questions from an existing run
 * and re-evaluates them with a different InceptBench version
 */
const ReEvaluateButton = ({ run }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [availableVersions, setAvailableVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Check if run can be re-evaluated (completed or failed with generated items)
  const canReEvaluate =
    run &&
    (run.status === "completed" || run.status === "failed") &&
    run.items_generated > 0;

  // Load available InceptBench versions when modal opens
  useEffect(() => {
    if (showModal) {
      loadVersions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const loadVersions = async () => {
    setLoadingVersions(true);
    try {
      const { data, error } = await fetchInceptBenchVersions();
      if (error) {
        throw error;
      }
      // Extract version strings from version objects
      const versionObjects = data?.versions || [];
      const versions = versionObjects.map((v) => v.version);
      setAvailableVersions(versions);

      // Pre-select a different version if current version is known
      if (run.inceptbench_version && versions.length > 0) {
        const otherVersion = versions.find(
          (v) => v !== run.inceptbench_version
        );
        if (otherVersion) {
          setSelectedVersion(otherVersion);
        } else if (versions.length > 0) {
          setSelectedVersion(versions[0]);
        }
      } else if (versions.length > 0) {
        setSelectedVersion(versions[0]);
      }
    } catch (err) {
      console.error("Error loading InceptBench versions:", err);
      setError("Failed to load InceptBench versions");
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleReEvaluate = async () => {
    if (!selectedVersion) {
      setError("Please select an InceptBench version");
      return;
    }

    if (!user) {
      setError("You must be logged in to create a run");
      return;
    }

    setIsCreating(true);
    setError(null);
    setSuccess(false);

    try {
      // Create new run with source_run_id set
      const result = await createRun(
        run.generator_id,
        run.filters,
        user.id,
        run.dataset_version,
        selectedVersion,
        run.curriculum_version || null, // curriculumVersion
        run.id // sourceRunId
      );

      if (result.error) {
        throw new Error(result.error.message || "Failed to create run");
      }

      setSuccess(true);

      // Poke the global queue. The scheduler will start this run if a slot
      // is free, otherwise the cron safety-net will pick it up within 30s.
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session) {
          const supabaseUrl = supabase.supabaseUrl;
          await fetch(`${supabaseUrl}/functions/v1/process-queue`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({}),
          });
        }
      } catch (queueError) {
        console.error("Error triggering queue processor:", queueError);
        // Non-fatal - the cron will pick it up
      }

      // Navigate to past runs page after a brief delay
      setTimeout(() => {
        navigate("/past-runs");
      }, 1500);
    } catch (err) {
      console.error("Error creating re-evaluation run:", err);
      setError(err.message || "Failed to create re-evaluation run");
    } finally {
      setIsCreating(false);
    }
  };

  if (!canReEvaluate) {
    return null;
  }

  return (
    <>
      {/* Re-Evaluate Button */}
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 transition-all hover:border-indigo-300 hover:bg-indigo-100 hover:shadow-md active:scale-95"
        title="Re-evaluate with different InceptBench version"
      >
        <FiRefreshCw className="size-4" />
        Re-evaluate on Another Version
      </button>

      {/* Re-Evaluate Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          {/* Backdrop */}
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            onClick={() => !isCreating && setShowModal(false)}
            disabled={isCreating}
          />

          {/* Modal */}
          <div className="relative w-full max-w-lg rounded-xl border border-gray-200 bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-indigo-100">
                  <FiRefreshCw className="size-5 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Re-evaluate on Another Version
                  </h3>
                  <p className="text-sm text-gray-500">
                    Reuse questions, evaluate with new version
                  </p>
                </div>
              </div>
              {!isCreating && (
                <button
                  onClick={() => setShowModal(false)}
                  className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                >
                  <FiX className="size-5" />
                </button>
              )}
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              {/* Info Box */}
              <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <p className="text-sm text-blue-800">
                  This will create a new run that:
                </p>
                <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-blue-700">
                  <li>
                    Reuses the {run.items_generated} generated questions from
                    this run
                  </li>
                  <li>Skips question generation entirely</li>
                  <li>Re-evaluates with the selected InceptBench version</li>
                  <li>Creates a new run record linked to this one</li>
                </ul>
              </div>

              {/* Current Version */}
              {run.inceptbench_version && (
                <div className="mb-4">
                  <div className="block text-sm font-medium text-gray-700">
                    Current Version
                  </div>
                  <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    InceptBench v{run.inceptbench_version}
                  </div>
                </div>
              )}

              {/* Version Selection */}
              <div className="mb-4">
                <label
                  htmlFor="version-select"
                  className="block text-sm font-medium text-gray-700"
                >
                  New InceptBench Version
                </label>
                <select
                  id="version-select"
                  value={selectedVersion}
                  onChange={(e) => setSelectedVersion(e.target.value)}
                  disabled={loadingVersions || isCreating}
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  {loadingVersions ? (
                    <option>Loading versions...</option>
                  ) : availableVersions.length === 0 ? (
                    <option>No versions available</option>
                  ) : (
                    availableVersions.map((version) => (
                      <option key={version} value={version}>
                        InceptBench v{version}
                        {version === run.inceptbench_version
                          ? " (current)"
                          : ""}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {/* Success Message */}
              {success && (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4">
                  <div className="flex items-center gap-2 text-green-800">
                    <FiCheck className="size-5" />
                    <span className="font-medium">
                      Re-evaluation run created successfully!
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-green-700">
                    Navigating to the new run...
                  </p>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-center gap-2 text-red-800">
                    <FiAlertCircle className="size-5" />
                    <span className="font-medium">Failed to create run</span>
                  </div>
                  <p className="mt-1 text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowModal(false)}
                disabled={isCreating || success}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReEvaluate}
                disabled={
                  isCreating ||
                  success ||
                  !selectedVersion ||
                  loadingVersions ||
                  availableVersions.length === 0
                }
                className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {success ? (
                  <>
                    <FiCheck className="size-4" />
                    Created!
                  </>
                ) : isCreating ? (
                  <>
                    <div className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <FiRefreshCw className="size-4" />
                    Create Re-evaluation Run
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

ReEvaluateButton.propTypes = {
  run: PropTypes.shape({
    id: PropTypes.string.isRequired,
    generator_id: PropTypes.string,
    filters: PropTypes.object,
    dataset_version: PropTypes.string,
    inceptbench_version: PropTypes.string,
    curriculum_version: PropTypes.string,
    status: PropTypes.string,
    items_generated: PropTypes.number,
  }).isRequired,
};

export default ReEvaluateButton;
